import { DistressLevels, Finishes, SheenTypes, WoodSpecies } from '../__generated/model/index.js';
export const WOOD_SPECIES_MAP = {
    [WoodSpecies.Hickory]: {
        type: WoodSpecies.Hickory,
        label: 'Hickory',
        thumbnailUrl: '/images/wood/HICKORY_NATURAL.jpg',
        pricing: {
            pricePercentageOfSize: 0
        }
    },
    [WoodSpecies.Ash]: {
        type: WoodSpecies.Ash,
        label: 'Ash',
        thumbnailUrl: '/images/wood/ASH_NATURAL.jpg',
        pricing: {
            pricePercentageOfSize: 0
        }
    },
    [WoodSpecies.Maple]: {
        type: WoodSpecies.Maple,
        label: 'Maple',
        thumbnailUrl: '/images/wood/MAPLE_NATURAL.jpg',
        pricing: {
            pricePercentageOfSize: 0.14
        }
    },
    [WoodSpecies.Walnut]: {
        type: WoodSpecies.Walnut,
        label: 'Walnut',
        thumbnailUrl: '/images/wood/WALNUT_NATURAL.jpg',
        pricing: {
            pricePercentageOfSize: 0.24
        }
    },
    [WoodSpecies.QuarterSawnWhiteOak]: {
        type: WoodSpecies.QuarterSawnWhiteOak,
        label: 'Quarter-Sawn White Oak',
        thumbnailUrl: '/images/wood/QUARTER_SAWN_WHITE_OAK_NATURAL.jpg',
        pricing: {
            pricePercentageOfSize: 0.35
        }
    }
};
export const DEFAULT_MATERIAL_PROPS = {
    roughness: 0.9,
    metalness: 0,
    envMapIntensity: 0.1,
    specularIntensity: 1,
    normalScale: 0.6,
    reflectivity: 0.5
};
export const TUMBLED_EDGE_NORMAL_MAP_URL = '/textures/tumbled_edge/tumbled-edge_Normal.jpg';
export const TUMBLED_EDGE_NORMAL_SCALE = 7;
export const TUMBLED_EDGE_REPEAT_RATIO = 25;
/**
 * UV compensation for the NaturalWavy edge model (`TOP_RECTANGLE_WAVY_EDGE`).
 * Its UV map is larger than the standard `TOP_RECTANGLE`, so the texture
 * appears smaller at the same repeat values. This multiplier is applied to
 * `$woodTextureSize` when the wavy model is active, scaling up the ratio
 * (fewer repeats → larger texture) to match the standard model's appearance.
 *
 * Increase this value if the texture still looks too small on wavy; decrease
 * if it looks too large.
 */ export const WAVY_EDGE_UV_SCALE = 1.15;
/**
 * Fixed physical tile size for the distress scratch texture, in inches (~76 cm).
 * The scratch bump map is always repeated at this physical scale regardless of
 * which wood species is selected, ensuring consistent-looking distress marks.
 */ export const DISTRESS_SCRATCH_REPEAT_RATIO_INCHES = 30;
export const DISTRESS_SCRATCHES_MAP = {
    [DistressLevels.Clean]: null,
    [DistressLevels.Minimal]: {
        url: '/textures/scratches/Scratches1.png',
        bumpScale: -4
    },
    [DistressLevels.Light]: {
        url: '/textures/scratches/Scratches2.png',
        bumpScale: -4
    },
    [DistressLevels.Medium]: {
        url: '/textures/scratches/Scratches3.png',
        bumpScale: -4
    },
    [DistressLevels.Heavy]: {
        url: '/textures/scratches/Scratches4.png',
        bumpScale: -4
    },
    [DistressLevels.RoughSawn]: {
        url: '/textures/scratches/Scratches5.png',
        bumpScale: -4
    }
};
export const WOOD_SPECIES_TEXTURE_URLS = {
    [WoodSpecies.Hickory]: {
        baseColor: '/textures/hickory3/hickory_BaseColor.jpg',
        normal: '/textures/hickory3/hickory_Normal.jpg',
        groovesNormal: '/textures/hickory3/hickory_grooves_Normal.webp',
        roughness: '/textures/hickory3/hickory_Roughness.jpg',
        ambientOcclusion: '/textures/hickory3/hickory_AO.jpg',
        textureSizeCm: 144,
        textureOffsetX: -0.03,
        materialProps: {
            roughness: 1.1,
            normalScale: 0.3
        }
    },
    [WoodSpecies.Ash]: {
        baseColor: '/textures/ash3/ash_BaseColor.jpg',
        normal: '/textures/ash3/ash_Normal.jpg',
        groovesNormal: '/textures/ash3/ash_grooves_Normal.webp',
        roughness: '/textures/ash3/ash_Roughness.jpg',
        ambientOcclusion: '/textures/ash3/ash_AO.jpg',
        textureSizeCm: 60,
        textureOffsetX: -0.03,
        materialProps: {
            roughness: 0.6,
            normalScale: 0.5
        }
    },
    [WoodSpecies.Maple]: {
        baseColor: '/textures/maple/maple_BaseColor.jpg',
        normal: '/textures/maple/maple_Normal.jpg',
        groovesNormal: '/textures/maple/maple_grooves_Normal.webp',
        roughness: '/textures/maple/maple_Roughness.jpg',
        ambientOcclusion: '/textures/maple/maple_AO.jpg',
        textureSizeCm: 120,
        textureOffsetX: -0.03,
        materialProps: {
            normalScale: 0.1,
            roughness: 1,
            metalness: 0.3,
            envMapIntensity: 0.5
        }
    },
    [WoodSpecies.Walnut]: {
        baseColor: '/textures/walnut2/walnut_BaseColor.jpg',
        normal: '/textures/walnut2/walnut_Normal.jpg',
        groovesNormal: '/textures/walnut2/walnut_grooves_Normal.webp',
        roughness: '/textures/walnut2/walnut_Roughness.jpg',
        ambientOcclusion: '/textures/walnut2/walnut_AO.jpg',
        textureSizeCm: 180,
        textureOffsetX: 0.01
    },
    [WoodSpecies.QuarterSawnWhiteOak]: {
        baseColor: '/textures/oak4/oak_BaseColor.jpg',
        normal: '/textures/oak4/oak_Normal.jpg',
        groovesNormal: '/textures/oak4/oak_grooves_Normal.webp',
        roughness: '/textures/oak4/oak_Roughness.jpg',
        ambientOcclusion: '/textures/oak4/oak_AO.jpg',
        textureSizeCm: 70,
        textureOffsetX: 0.03,
        materialProps: {
            roughness: 1.1
        }
    }
};
export const METAL_TEXTURE_URLS = {
    baseColor: '/textures/metal/metal_BaseColor.jpg',
    normal: '/textures/metal/metal_Normal.jpg',
    roughness: '/textures/empty/empty_Gray.png',
    ambientOcclusion: '/textures/empty/empty_White.png',
    textureSizeCm: 108,
    materialProps: {
        roughness: 0.7,
        metalness: 0.4,
        specularIntensity: 0.8,
        reflectivity: 0
    }
};
const SHEEN_MODIFIERS = {
    [SheenTypes.Flat]: {},
    [SheenTypes.Satin]: {
        roughness: 0.7,
        reflectivity: 0.85
    }
};
function applySheenModifiers(props, sheen) {
    const modifiers = SHEEN_MODIFIERS[sheen];
    if (!modifiers) return props;
    const result = {
        ...props
    };
    for (const key of Object.keys(modifiers)){
        result[key] = props[key] * modifiers[key];
    }
    return result;
}
export function getWoodMaterialProps(species, sheen) {
    const base = {
        ...DEFAULT_MATERIAL_PROPS,
        ...WOOD_SPECIES_TEXTURE_URLS[species]?.materialProps
    };
    return sheen ? applySheenModifiers(base, sheen) : base;
}
export function getMetalMaterialProps() {
    return {
        ...DEFAULT_MATERIAL_PROPS,
        ...METAL_TEXTURE_URLS.materialProps
    };
}
export const FINISH_MAP = {
    [Finishes.BlackTone]: {
        type: Finishes.BlackTone,
        label: 'Black Tone',
        colorHex: '#2b2b2b'
    },
    [Finishes.EbonyTone]: {
        type: Finishes.EbonyTone,
        label: 'Ebony Tone',
        colorHex: '#423434'
    },
    [Finishes.Gray]: {
        type: Finishes.Gray,
        label: 'Gray',
        colorHex: '#87847e'
    },
    [Finishes.DarkBrownTone]: {
        type: Finishes.DarkBrownTone,
        label: 'Dark Brown Tone',
        colorHex: '#574533'
    },
    [Finishes.MediumBrownTone]: {
        type: Finishes.MediumBrownTone,
        label: 'Medium Brown Tone',
        colorHex: '#7d5f3c'
    },
    [Finishes.LightBrownTone]: {
        type: Finishes.LightBrownTone,
        label: 'Light Brown Tone',
        colorHex: '#ad9580'
    },
    [Finishes.NaturalTone]: {
        type: Finishes.NaturalTone,
        label: 'Natural Tone',
        colorHex: '#c8b7a1'
    },
    [Finishes.Earthtone]: {
        type: Finishes.Earthtone,
        label: 'Earthtone',
        colorHex: '#9d7f68'
    },
    [Finishes.Taupe]: {
        type: Finishes.Taupe,
        label: 'Taupe',
        colorHex: '#887667'
    },
    [Finishes.Custom]: {
        type: Finishes.Custom,
        label: 'Custom',
        colorHex: '#e2dfd9'
    }
};
export const METAL_FINISH_COLOR_HEX = '#8A8A8A';
export const SHEEN_MAP = {
    [SheenTypes.Flat]: {
        type: SheenTypes.Flat,
        label: 'Flat',
        value: SheenTypes.Flat
    },
    [SheenTypes.Satin]: {
        type: SheenTypes.Satin,
        label: 'Satin',
        value: SheenTypes.Satin
    }
};
export const TEXTURES_URLS = [
    WOOD_SPECIES_TEXTURE_URLS.Hickory.baseColor,
    WOOD_SPECIES_TEXTURE_URLS.Hickory.normal,
    WOOD_SPECIES_TEXTURE_URLS.Hickory.groovesNormal,
    WOOD_SPECIES_TEXTURE_URLS.Hickory.roughness,
    WOOD_SPECIES_TEXTURE_URLS.Hickory.ambientOcclusion,
    WOOD_SPECIES_TEXTURE_URLS.Ash.baseColor,
    WOOD_SPECIES_TEXTURE_URLS.Ash.normal,
    WOOD_SPECIES_TEXTURE_URLS.Ash.groovesNormal,
    WOOD_SPECIES_TEXTURE_URLS.Ash.roughness,
    WOOD_SPECIES_TEXTURE_URLS.Ash.ambientOcclusion,
    WOOD_SPECIES_TEXTURE_URLS.Maple.baseColor,
    WOOD_SPECIES_TEXTURE_URLS.Maple.normal,
    WOOD_SPECIES_TEXTURE_URLS.Maple.groovesNormal,
    WOOD_SPECIES_TEXTURE_URLS.Maple.roughness,
    WOOD_SPECIES_TEXTURE_URLS.Maple.ambientOcclusion,
    WOOD_SPECIES_TEXTURE_URLS.Walnut.baseColor,
    WOOD_SPECIES_TEXTURE_URLS.Walnut.normal,
    WOOD_SPECIES_TEXTURE_URLS.Walnut.groovesNormal,
    WOOD_SPECIES_TEXTURE_URLS.Walnut.roughness,
    WOOD_SPECIES_TEXTURE_URLS.Walnut.ambientOcclusion,
    WOOD_SPECIES_TEXTURE_URLS.QuarterSawnWhiteOak.baseColor,
    WOOD_SPECIES_TEXTURE_URLS.QuarterSawnWhiteOak.normal,
    WOOD_SPECIES_TEXTURE_URLS.QuarterSawnWhiteOak.groovesNormal,
    WOOD_SPECIES_TEXTURE_URLS.QuarterSawnWhiteOak.roughness,
    WOOD_SPECIES_TEXTURE_URLS.QuarterSawnWhiteOak.ambientOcclusion,
    METAL_TEXTURE_URLS.baseColor,
    METAL_TEXTURE_URLS.normal,
    METAL_TEXTURE_URLS.roughness,
    METAL_TEXTURE_URLS.ambientOcclusion,
    TUMBLED_EDGE_NORMAL_MAP_URL,
    ...Object.values(DISTRESS_SCRATCHES_MAP).filter((entry)=>entry !== null).map((entry)=>entry.url)
];

//# sourceMappingURL=texture-data.js.map